import { useEffect } from 'react';
import type { PaywallInfo } from './paywall';

/**
 * One channel for every HTTP 402 in the app.
 *
 * Only four pages ever rendered a 402 themselves (Case Search, Constitution,
 * Case Theory, New Matter). Everywhere else — Search Laws, FIR Analyzer,
 * Hearing Prep, the bail checklist, notice analyzer, forum calculator — a
 * lapsed lawyer got a generic error and was never offered Renew (2026-10-20).
 * The axios interceptor and the SSE client publish here; GlobalPaywallHost
 * shows it. A page that explains the 402 in place (PaywallNotice) registers
 * itself, and the global notice stays quiet so the lawyer never sees it twice.
 */
type Listener = (info: PaywallInfo) => void;

const listeners = new Set<Listener>();
let inlineNotices = 0;

export const paywallBus = {
  publish(info: PaywallInfo): void {
    if (inlineNotices > 0) return;
    listeners.forEach((l) => l(info));
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** Called by PaywallNotice while mounted. Returns the unregister function. */
  registerInline(): () => void {
    inlineNotices += 1;
    return () => {
      inlineNotices = Math.max(0, inlineNotices - 1);
    };
  },
};

/** For a page that explains its own 402s in place: keeps the app-wide notice
 *  quiet while the page is mounted, so the lawyer never sees the same 402 twice. */
export function useInlinePaywall(): void {
  useEffect(() => paywallBus.registerInline(), []);
}
